<template>
  <div class="modal fade" id="employee_bulk_login_modal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">
            {{ allReady ? "Invite All Ready Employees" : "Enable Portal Access" }}
          </h4>
          <button type="button" class="btn-close custom-btn-close" data-bs-dismiss="modal" aria-label="Close">
            <i class="ti ti-x"></i>
          </button>
        </div>

        <div class="modal-body">
          <p class="text-muted mb-3">
            Create login accounts for Active employees with a real email and no portal user.
            Each person receives a <strong>set-password link</strong> (valid 7 days) — no temporary passwords.
          </p>

          <div class="row g-3 mb-3">
            <div class="col-md-3 col-6">
              <div class="border rounded p-3 h-100">
                <div class="text-muted fs-12">{{ allReady ? "Scope" : "Selected" }}</div>
                <div class="fs-18 fw-semibold">
                  {{ allReady ? "All ready" : employeeIds.length }}
                </div>
              </div>
            </div>
            <div class="col-md-3 col-6">
              <div class="border rounded p-3 h-100">
                <div class="text-muted fs-12">Ready</div>
                <div class="fs-18 fw-semibold text-success">{{ summary.ready || 0 }}</div>
              </div>
            </div>
            <div class="col-md-3 col-6">
              <div class="border rounded p-3 h-100">
                <div class="text-muted fs-12">Skipped</div>
                <div class="fs-18 fw-semibold">{{ summary.skipped || 0 }}</div>
              </div>
            </div>
            <div class="col-md-3 col-6">
              <div class="border rounded p-3 h-100">
                <div class="text-muted fs-12">Errors</div>
                <div class="fs-18 fw-semibold text-danger">{{ summary.errors || 0 }}</div>
              </div>
            </div>
          </div>

          <div class="form-check mb-3">
            <input
              id="bulk_send_welcome"
              v-model="sendWelcomeEmail"
              class="form-check-input"
              type="checkbox"
            />
            <label class="form-check-label" for="bulk_send_welcome">
              Send welcome email with set-password link
            </label>
          </div>

          <div class="table-responsive border rounded" style="max-height: 360px; overflow: auto">
            <table class="table table-sm mb-0">
              <thead>
                <tr>
                  <th>Payroll No.</th>
                  <th>Employee</th>
                  <th>Email</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loadingPreview">
                  <td colspan="4" class="text-center text-muted py-3">Checking employees...</td>
                </tr>
                <tr v-else-if="!rows.length">
                  <td colspan="4" class="text-center text-muted py-3">
                    {{ allReady ? "No employees are ready to invite." : "Select employees first." }}
                  </td>
                </tr>
                <tr v-for="row in rows" :key="row.employee_db_id">
                  <td>{{ row.payroll_number }}</td>
                  <td>{{ row.employee_name }}</td>
                  <td>{{ row.email || "—" }}</td>
                  <td>
                    <span class="badge" :class="statusClass(row.status)">{{ statusLabel(row.status) }}</span>
                    <div class="small text-muted mt-1">{{ row.message }}</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancel</button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="loading || loadingPreview || (summary.ready || 0) === 0"
            @click="runApply"
          >
            {{
              loading
                ? "Inviting..."
                : `Invite ${summary.ready || 0} Employee(s)`
            }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from "vue";
import { Modal } from "bootstrap";
import employeeService from "@/services/hrm/employeeService";
import { toast } from "@/utils/toast";

const props = defineProps({
  employeeIds: { type: Array, default: () => [] },
  allReady: { type: Boolean, default: false },
});

const emit = defineEmits(["invited"]);

const loading = ref(false);
const loadingPreview = ref(false);
const sendWelcomeEmail = ref(true);
const rows = ref([]);
const summary = ref({});

function onModalShow() {
  if (props.allReady || props.employeeIds.length) {
    runPreview();
  } else {
    rows.value = [];
    summary.value = {};
  }
}

watch(
  () => [props.allReady, ...props.employeeIds],
  () => {
    const modalEl = document.getElementById("employee_bulk_login_modal");
    if (modalEl?.classList.contains("show")) {
      onModalShow();
    }
  },
);

onMounted(() => {
  const modalEl = document.getElementById("employee_bulk_login_modal");
  modalEl?.addEventListener("show.bs.modal", onModalShow);
});

onBeforeUnmount(() => {
  const modalEl = document.getElementById("employee_bulk_login_modal");
  modalEl?.removeEventListener("show.bs.modal", onModalShow);
});

async function runPreview() {
  loadingPreview.value = true;
  try {
    const { data } = await employeeService.previewBulkProvisionLogin(
      props.allReady ? [] : props.employeeIds,
      props.allReady,
    );
    rows.value = Array.isArray(data?.rows) ? data.rows : [];
    summary.value = data?.summary || {};
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to preview login invites.", "error");
  } finally {
    loadingPreview.value = false;
  }
}

async function runApply() {
  const readyCount = summary.value.ready || 0;
  if (!readyCount) return;

  const confirmMsg = props.allReady
    ? `Invite all ${readyCount} ready employee(s)? Each will get a set-password email.`
    : `Invite ${readyCount} selected employee(s)? Each will get a set-password email.`;

  if (!window.confirm(confirmMsg)) return;

  const readyIds = rows.value
    .filter((r) => r.status === "ready")
    .map((r) => r.employee_db_id);

  loading.value = true;
  try {
    const { data } = await employeeService.bulkProvisionLogin(
      props.allReady ? [] : readyIds,
      sendWelcomeEmail.value,
      props.allReady,
    );
    toast(data?.message || "Portal access updated.", "success");
    emit("invited");
    const modalEl = document.getElementById("employee_bulk_login_modal");
    if (modalEl) {
      Modal.getOrCreateInstance(modalEl).hide();
    }
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to enable portal access.", "error");
  } finally {
    loading.value = false;
  }
}

function statusClass(status) {
  if (status === "ready" || status === "invited") return "badge-soft-success";
  if (status === "skip") return "badge-soft-secondary";
  return "badge-soft-danger";
}

function statusLabel(status) {
  if (status === "ready") return "Ready";
  if (status === "invited") return "Invited";
  if (status === "skip") return "Skipped";
  return "Error";
}

defineExpose({ runPreview });
</script>
